import { useEffect, useMemo, useRef, useState } from 'react'
import { Betrag } from './Betrag'
import { api, betrag as formatiert, periodenname } from './api'
import { Auswahlfeld, kontoname, mitCode } from './Auswahlfeld'
import { Kontofeld } from './Kontofeld'
import { ohneTechnische } from './Festkonten'
import { Dialogfenster } from './Dialogfenster'
import { fachlicherSatz } from './Fehlersatz'
import { beschriftung, bewertungsebenenname, gesellschaftsname, katalog, kontobezeichnung, t } from './sprache'
import { ohneWert } from './Ohnewert'
import { Periodenfeld } from './Periodenfeld'
import { useDarf } from './Rechte'
import { Spaltenmassleiste, useSpaltenmasse } from './Spaltenbreiten'
import { ausPeriodenfeld } from './Zeitraum'
import type {
  Buchbaresonto,
  Buchungsrahmen,
  Erfassungsauftrag,
  Erfassungszeile,
  Vorratszeile,
} from './typen'

/**
 * Die Buchungsmaske — Soll an Haben, und es geht auf (2.16).
 *
 * **Zwei Betragsspalten und nicht ein Feld mit Vorzeichen** (R370). Gespeichert
 * wird Soll-positiv, wie überall; aber ein Fachanwender aus dem
 * Rechnungswesen denkt in Soll und Haben, und ein Minuszeichen, das je nach
 * Kontoart etwas anderes bedeutet, ist die häufigste Fehlerquelle beim
 * Handbuchen. Die Umrechnung geschieht hier, an genau einer Stelle.
 *
 * **Die Pflichtdimensionen sind sechs** (R367): Konto · Periode · Betrag ·
 * Bewegungsart · Bewertungsebene · Partner. Die **Gesellschaft ist die siebte und kommt
 * aus dem Ordner** — das Journal wird Gesellschaft für Gesellschaft geführt,
 * und deshalb steht sie am Kopf und nicht in einem Feld (R373).
 *
 * **Alles Übrige ist da und ausgeblendet** (R369): Ankreuzfelder holen Segment,
 * Funktionsbereich, Einbeziehungsart, Transaktionswährung, Nachbuchung und die
 * acht Analysedimensionen einzeln als Spalte herein. Eine Buchungsmaske mit
 * einundzwanzig Spalten ab Werk wäre derselbe Fehler wie ein Wertenachweis,
 * der starr alles zeigt — nur an der Eingabe statt an der Ausgabe.
 */

/** Woher die Maske gerufen wurde und was sie davon übernimmt (R385). */
export type Buchungsvorbelegung = {
  einheit: string
  ledger: string
  periode: number
  kreis?: string | null
  konto?: string | null
  /** Was der Aufriss über Konto und Gesellschaft hinaus festgelegt hat. */
  weitere?: Record<string, string | null>
  /** In welchen Ordner der Beleg soll — sonst in den Eingang. */
  ordnerId?: number | null
  /**
   * Die Zelle lag in einer Konsolidierungsspalte: Dort stehen gerechnete
   * Werte, und vorbelegt wird deshalb nichts außer Periode und Bewertungsebene (R385).
   */
  ausKonsolidierungsspalte?: boolean
  /** Eine Vorlage aus der Zwischenablage — Merkmale ohne Werte (R376). */
  vorlage?: Erfassungszeile[]
  /** Ein Duplikat aus der Zwischenablage — Merkmale samt Werten (R376). */
  duplikat?: { name: string; erlaeuterung: string; zeilen: Erfassungszeile[] }
  /**
   * **Einen vorhandenen Beleg berichtigen** (R388) — der Zahlendreher von
   * gestern.
   *
   * Die Maske ist dieselbe; nur der Kopf sagt es, die Klammer ist
   * unveränderlich, und gespeichert wird über einen anderen Weg. Eine zweite
   * Maske fürs Berichtigen wäre eine zweite Gelegenheit, eine Prüfung zu
   * vergessen.
   */
  bearbeiten?: {
    belegId: number
    nummer: string
    name: string
    erlaeuterung: string
    begruendung: string
    zeilen: Erfassungszeile[]
  }
}

/** Eine Zeile, wie die Maske sie führt: getrennt nach Soll und Haben. */
type Maskenzeile = {
  id: number
  konto: string
  bewegungsart: string
  icPartner: string
  /** Ob der Partner ausdrücklich gewählt wurde — „Default" ist eine Wahl (R368, R429). */
  partnerGewaehlt: boolean
  soll: string
  haben: string
  zusatz: Record<string, string>
}

/** Die ausblendbaren Spalten (R369) — feste zuerst, Dimensionen danach. */
type Zusatzspalte = {
  id: string
  kopf: string
  art: 'auswahl' | 'text' | 'periode'
  vorrat?: Vorratszeile[]
  /** Eine Dimension, die eingerichtet und hier nicht belegt ist, wird ausgegraut (R369). */
  leer: boolean
}

const MERKSCHLUESSEL = 'coko.belegmaske.spalten'

/** Was „ohne Bewegungsart" heißt — in der Lieferung wie hier (R1071). */
const DEFAULT_BEWEGUNGSART = 'BWA-DEF'

let naechsteId = 1

function leereZeile(): Maskenzeile {
  return {
    id: naechsteId++,
    konto: '',
    bewegungsart: '',
    icPartner: '',
    partnerGewaehlt: false,
    soll: '',
    haben: '',
    zusatz: {},
  }
}

function zahl(text: string): number {
  const bereinigt = text.replace(/\s/g, '').replace(/\./g, '').replace(',', '.')
  const wert = Number(bereinigt)
  return Number.isFinite(wert) ? wert : 0
}

export function Belegmaske({
  vorbelegung,
  aufSchliessen,
  aufGebucht,
}: {
  vorbelegung: Buchungsvorbelegung
  aufSchliessen: () => void
  /** Die Nummer und ob berichtigt statt neu gebucht wurde — die Meldung ist eine andere. */
  aufGebucht: (nummer: string, berichtigt?: boolean) => void
}) {
  /*
    **Die Spaltenbreiten zieht der Anwender** (7.3 Abschnitt 10, R415 —
    Auftrag vom 2026-09-29). Hier je Spalte eine Breite: Die Spalten dieser
    Tabelle tragen verschiedene Dinge und haben nichts gleichzumachen.
  */
  const { masse: spaltenmasse, rahmen: spaltenrahmen } =
    useSpaltenmasse<HTMLTableElement>('belegmaske:belegmaske')

  const [rahmen, setRahmen] = useState<Buchungsrahmen | null>(null)
  const [fehler, setFehler] = useState<string | null>(null)
  const [laeuft, setLaeuft] = useState(false)
  /** Die Zeile, in der der Anwender zuletzt stand — sie gleicht „Ausgleichen" aus (R1053). */
  const [zuletzt, setZuletzt] = useState<number | null>(null)
  // Ohne Recht kein Speichern-Knopf — und damit auch kein Strg+S (R1180).
  const darf = useDarf()
  const darfSpeichern = vorbelegung.bearbeiten ? darf('belegAendern') : darf('buchen')

  /*
    **Gebucht wird in die Schicht, nicht in die Gruppe** (2.16 Abschnitt 3.6,
    R1048). Aus der Kontextzeile kommt die Gruppe — „HGB" —, und vorbelegt
    ist, was der Rahmen daraus macht: ihre Anpassungsschicht. Wählt der
    Anwender eine andere, kommt der Rahmen für sie neu, denn die Gliederung
    und damit die zulässigen Bewegungsarten können andere sein. Beim
    Berichtigen steht die Ebene des Belegs fest (R388).
  */
  const [gewaehlteEbene, setGewaehlteEbene] = useState<string | null>(null)
  const ebene = vorbelegung.bearbeiten
    ? vorbelegung.ledger
    : (gewaehlteEbene ?? rahmen?.ledger ?? null)

  const [name, setName] = useState(
    vorbelegung.bearbeiten?.name ?? vorbelegung.duplikat?.name ?? '',
  )
  const [erlaeuterung, setErlaeuterung] = useState(
    vorbelegung.bearbeiten?.erlaeuterung ?? vorbelegung.duplikat?.erlaeuterung ?? '',
  )
  const [begruendung, setBegruendung] = useState(vorbelegung.bearbeiten?.begruendung ?? '')

  const [zeilen, setZeilen] = useState<Maskenzeile[]>(() => anfangszeilen(vorbelegung))
  const [sichtbar, setSichtbar] = useState<string[]>(() => gemerkteSpalten())

  const buchenKnopf = useRef<HTMLButtonElement | null>(null)
  const erstesFeld = useRef<HTMLInputElement | null>(null)

  useEffect(() => {
    let gilt = true
    api
      .buchungsrahmen(vorbelegung.einheit, gewaehlteEbene ?? vorbelegung.ledger, vorbelegung.periode)
      .then((r) => gilt && setRahmen(r))
      // Kein Pflichtfeld fehlt, wenn der Rahmen nicht kommt: Eine Störung
      // meldet das Störungsfenster, hier steht nur ein fachlicher Satz
      // (7.2 Abschnitt 30, R1003/R1004).
      .catch((f) => gilt && setFehler(fachlicherSatz(f)))

    return () => {
      gilt = false
    }
  }, [vorbelegung.einheit, vorbelegung.ledger, vorbelegung.periode, gewaehlteEbene])

  // Escape schließt, Strg+S bucht — dieselbe Bedienung wie im Wertenachweis.
  // Eine Funktion, die nur die Maus kennt, gibt es nicht (R143). Escape hört
  // seit dem 2026-10-06 das übliche Fenster (7.2 Abschnitt 41, R1260); der
  // Knopf zum Buchen steht in dessen Fuß, deshalb greift Strg+S über einen
  // eigenen Verweis und nicht mehr über die Maske.
  useEffect(() => {
    erstesFeld.current?.focus()

    function beiTaste(e: KeyboardEvent) {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 's') {
        e.preventDefault()
        buchenKnopf.current?.click()
      }
    }

    window.addEventListener('keydown', beiTaste)
    return () => window.removeEventListener('keydown', beiTaste)
  }, [])

  const nachKonto = useMemo(() => {
    const karte = new Map<string, Buchbaresonto>()
    for (const k of rahmen?.konten ?? []) {
      karte.set(k.schluessel, k)
    }
    return karte
  }, [rahmen])

  const nachArt = useMemo(() => {
    const karte = new Map<string, Vorratszeile>()
    for (const a of rahmen?.bewegungsarten ?? []) {
      karte.set(a.schluessel, a)
    }
    return karte
  }, [rahmen])

  /*
    **Die Kontenliste ist die längste der Anwendung** (7.2 Abschnitt 10, R583).

    In der Entwicklungsdatenbank stehen 50 Konten, in einem Kontenrahmen des
    Kunden viertausend. Eine Zeile zu erfassen hieß bisher, sie mit den Augen
    zu suchen — und zwar in einem Feld, das in seiner Tabellenzelle 90 px breit
    ist. Gebaut wird die Liste einmal je Rahmen und nicht je Zeile: Bei zehn
    Buchungszeilen wäre es sonst zehnmal dieselbe Arbeit.
  */
  const kontenwahl = useMemo(
    () =>
      // Auf die technischen Konten bucht nur CoKo (R1427).
      ohneTechnische(rahmen?.konten ?? [], (k) => k.schluessel).map((k) => ({
        wert: k.schluessel,
        text: kontoname(k),
        suchtext: [k.schluessel, ...Object.values(k.namen)].join(' '),
        stillgelegtAb: k.stillgelegtAb,
      })),
    [rahmen],
  )

  // Der Partner trägt zwei Sonderzeilen vor dem Bestand: den unentschiedenen
  // Zustand (R368) und den ausdrücklichen Vorgabewert (R429). Beide sind Wahl
  // und nicht Abwesenheit — deshalb stehen sie in der Liste und nicht daneben.
  const partnerwahl = useMemo(
    () => [
      { wert: '?', text: '—' },
      { wert: '', text: ohneWert('weitere') },
      ...(rahmen?.partner ?? []).map((p) => ({
        wert: p.schluessel,
        text: mitCode(p.code, beschriftung(p.namen), ' ', p.schluessel),
        suchtext: [p.schluessel, ...Object.values(p.namen)].join(' '),
        stillgelegtAb: p.stillgelegtAb,
      })),
    ],
    [rahmen],
  )

  /*
    **Die Spalten auf Abruf** (R369). Sie entstehen aus dem Rahmen und nicht
    aus einer festen Liste: Welche Analysedimensionen es gibt und wie sie
    heißen, sagt der Kunde (B9). Eine fest eingebaute „Kostenstelle" wäre
    genau der Fehler, den 2.4 vermeidet.
  */
  const zusatzspalten = useMemo<Zusatzspalte[]>(() => {
    if (rahmen === null) {
      return []
    }

    const feste: Zusatzspalte[] = [
      {
        id: 'segment',
        kopf: t('beleg.spalte.segment'),
        art: 'auswahl',
        vorrat: rahmen.segmente,
        leer: rahmen.segmente.length === 0,
      },
      {
        id: 'funktionsbereich',
        kopf: t('beleg.spalte.funktionsbereich'),
        art: 'auswahl',
        vorrat: rahmen.funktionsbereiche,
        leer: rahmen.funktionsbereiche.length === 0,
      },
      {
        id: 'transaktionswaehrung',
        kopf: t('beleg.spalte.transaktionswaehrung'),
        art: 'text',
        leer: false,
      },
      {
        id: 'nachbuchungPeriode',
        kopf: t('beleg.spalte.nachbuchung'),
        art: 'periode',
        leer: false,
      },
      { id: 'erlaeuterung', kopf: t('beleg.spalte.zeilentext'), art: 'text', leer: false },
    ]

    const dimensionen = rahmen.dimensionen.map<Zusatzspalte>((d) => ({
      id: `dim${d.nummer}`,
      kopf: beschriftung(d.namen) || d.code,
      art: 'auswahl',
      vorrat: d.werte,
      // Eine Dimension, die es gibt und die hier nirgends belegt ist, wird
      // angeboten und ausgegraut — dass sie leer ist, ist eine Auskunft.
      leer: d.werte.length === 0,
    }))

    return [...feste, ...dimensionen]
  }, [rahmen])

  const summeSoll = zeilen.reduce((s, z) => s + zahl(z.soll), 0)
  const summeHaben = zeilen.reduce((s, z) => s + zahl(z.haben), 0)
  const differenz = Math.round((summeSoll - summeHaben) * 100) / 100

  const belegt = zeilen.filter((z) => z.konto !== '' && (zahl(z.soll) !== 0 || zahl(z.haben) !== 0))

  /*
    **Der Statistikbeleg** (2.1 Abschnitt 3.6, R1726, R1732): Tragen alle
    gewählten Konten die Kontoart Statistik, gibt es kein Soll und Haben — der
    Betrag steht mit + oder − so da, wie er in der Kapitalflussrechnung
    erscheinen soll, und eine Zeile genügt. Er steht im Feld `soll`, mit
    seinem Vorzeichen; gedreht wird nichts. Steht ein Statistikkonto neben
    einem gewöhnlichen, bucht die Maske nicht und sagt warum.
  */
  const istStatistik = (konto: string) => nachKonto.get(konto)?.kontoart === 'statistik'
  const gewaehlt = zeilen.filter((z) => z.konto !== '')
  const statistikbeleg = gewaehlt.length > 0 && gewaehlt.every((z) => istStatistik(z.konto))
  const gemischt = !statistikbeleg && gewaehlt.some((z) => istStatistik(z.konto))

  /*
    **Der Hinweis nennt, was fehlt** (R374, R388). Bis zum 2026-10-05 stand
    nur „Name, Erläuterung und Begründung sind Pflicht." da — beim Berichtigen
    war die Begründung leer, der Knopf grau, und wer Name und Erläuterung
    ausgefüllt sah, suchte den Grund in den Zeilen.
  */
  const fehlend = (
    [
      ['name', name],
      ['erlaeuterung', erlaeuterung],
      ['begruendung', begruendung],
    ] as const
  )
    .filter(([, wert]) => wert.trim() === '')
    .map(([feld]) => feld)

  const pflichtVollstaendig = fehlend.length === 0

  // Markiert wird erst, wenn gebucht wird — eine leere Maske ganz in Rot
  // wäre ein Vorwurf, bevor jemand etwas getan hat.
  const markiere = (feld: (typeof fehlend)[number]) =>
    (belegt.length > 0 || vorbelegung.bearbeiten !== undefined) && fehlend.includes(feld)

  // **Der Partner ist Pflicht als Entscheidung** (R368): Das Feld hat keinen
  // Vorgabewert, und „Default" muss ausdrücklich gewählt werden. Auf einem
  // Statistikkonto ist er wählbar, aber keine Pflicht (2.1 Abschnitt 3.6,
  // R1735): Ohne Wahl bleibt das Feld leer, ohne Markierung.
  const partnerOffen = belegt.some((z) => !z.partnerGewaehlt && !istStatistik(z.konto))

  // **Eine Nachbuchungsperiode, die sich nicht lesen lässt, hält die Buchung
  // auf** (R839, R145). Sonst ginge der Beleg ohne sie hinaus — und der
  // Anwender hätte eine getippt. Leer ist keine Fehleingabe: Dann gibt es
  // keine Nachbuchung.
  const periodeUnlesbar = belegt.some((z) => {
    const text = (z.zusatz.nachbuchungPeriode ?? '').trim()
    return text !== '' && ausPeriodenfeld(text) === null
  })

  const buchbar =
    rahmen !== null &&
    ebene !== null &&
    (statistikbeleg ? belegt.length >= 1 : belegt.length >= 2 && differenz === 0) &&
    !gemischt &&
    pflichtVollstaendig &&
    !partnerOffen &&
    !periodeUnlesbar &&
    !laeuft

  function aendere(id: number, teil: Partial<Maskenzeile>) {
    setZeilen((alt) => alt.map((z) => (z.id === id ? { ...z, ...teil } : z)))
  }

  /**
   * Der Betrag der Gegenseite steht als Vorschlag da, **das Konto nie**
   * (R386): Ein geratenes Gegenkonto wäre eine Buchung, die das Programm
   * gemacht hat und der Anwender unterschreibt.
   *
   * **Nur auf der Seite, die ausgleicht** (R1049). Bis zum 2026-10-04 stand
   * die offene Differenz ohne Vorzeichen in Soll und Haben zugleich — gegen
   * 300 Haben also zweimal 300, und eines davon war falsch.
   */
  function gegenvorschlag(id: number, seite: 'soll' | 'haben'): string {
    const rest = zeilen
      .filter((z) => z.id !== id)
      .reduce((s, z) => s + zahl(z.soll) - zahl(z.haben), 0)
    // Steht bei den anderen mehr im Soll, gleicht diese Zeile im Haben aus — und umgekehrt.
    const ausgleich = rest > 0 ? 'haben' : rest < 0 ? 'soll' : null
    return ausgleich === seite ? formatiert(Math.abs(rest)) : ''
  }

  /** Die Eingabetaste im leeren Betragsfeld übernimmt den Vorschlag (R1049). */
  function uebernimm(e: React.KeyboardEvent<HTMLInputElement>, z: Maskenzeile, seite: 'soll' | 'haben') {
    if (e.key !== 'Enter' || z[seite] !== '') {
      return
    }

    const vorschlag = z.soll === '' && z.haben === '' ? gegenvorschlag(z.id, seite) : ''
    if (vorschlag !== '') {
      e.preventDefault()
      aendere(z.id, seite === 'soll' ? { soll: vorschlag, haben: '' } : { haben: vorschlag, soll: '' })
    }
  }

  /*
    **Was „Ausgleichen" täte** (R1053): die Zeile, in der der Anwender zuletzt
    stand — vor dem ersten Klick die letzte —, auf den Betrag, der den Beleg
    ausgleicht, auf der richtigen Seite. Ein vorhandener Wert wird ersetzt;
    genau dafür ist der Knopf da, die Eingabetaste im leeren Feld reicht sonst.
  */
  const ziel = zeilen.find((z) => z.id === zuletzt) ?? zeilen[zeilen.length - 1]
  const ausgleich = (() => {
    if (!ziel || differenz === 0) {
      return null
    }

    const rest =
      Math.round(
        zeilen.filter((z) => z.id !== ziel.id).reduce((s, z) => s + zahl(z.soll) - zahl(z.haben), 0) * 100,
      ) / 100
    if (rest === 0) {
      return null
    }

    return {
      id: ziel.id,
      nummer: zeilen.indexOf(ziel) + 1,
      seite: rest > 0 ? ('haben' as const) : ('soll' as const),
      betrag: formatiert(Math.abs(rest)),
    }
  })()

  async function buche() {
    if (!buchbar || rahmen === null || ebene === null) {
      return
    }

    setLaeuft(true)
    setFehler(null)

    const auftrag: Erfassungsauftrag = {
      einheit: rahmen.einheit,
      ledger: ebene,
      periode: rahmen.periode,
      name: name.trim(),
      erlaeuterung: erlaeuterung.trim(),
      begruendung: begruendung.trim(),
      ordnerId: vorbelegung.ordnerId ?? null,
      kreis: vorbelegung.kreis ?? null,
      zeilen: belegt.map<Erfassungszeile>((z) => {
        const konto = nachKonto.get(z.konto)
        const soll = zahl(z.soll)
        const haben = zahl(z.haben)

        return {
          konto: z.konto,
          // **Hier und nur hier wird gedreht** (R370): Soll positiv, Haben
          // negativ. Gespeichert wird Soll-positiv, wie überall.
          betrag: Math.round((soll - haben) * 100) / 100,
          bewegungsart:
            konto?.mitBewegungsart === false ? null : z.bewegungsart || null,
          icPartner: z.icPartner || null,
          segment: z.zusatz.segment || null,
          funktionsbereich: z.zusatz.funktionsbereich || null,
          transaktionswaehrung: z.zusatz.transaktionswaehrung || null,
          // **Gelesen wie im Periodenfeld, gebucht in der Speicherform** (R839):
          // Bis zum 2026-09-30 stand hier `Number(...)`, und „11.2026" wurde
          // damit zur Zahl 11,2026.
          nachbuchungPeriode: ausPeriodenfeld(z.zusatz.nachbuchungPeriode ?? ''),
          erlaeuterung: z.zusatz.erlaeuterung || null,
          dim1: z.zusatz.dim1 || null,
          dim2: z.zusatz.dim2 || null,
          dim3: z.zusatz.dim3 || null,
          dim4: z.zusatz.dim4 || null,
          dim5: z.zusatz.dim5 || null,
          dim6: z.zusatz.dim6 || null,
          dim7: z.zusatz.dim7 || null,
          dim8: z.zusatz.dim8 || null,
        }
      }),
    }

    try {
      if (vorbelegung.bearbeiten) {
        // **Derselbe Auftrag, ein anderer Weg** (R388): Der Beleg behält
        // Nummer und Kennung; Gesellschaft, Bewertungsebene und Periode kommen ohnehin
        // vom Beleg und nicht aus der Maske.
        await api.belegAendern(vorbelegung.bearbeiten.belegId, auftrag)
        aufGebucht(vorbelegung.bearbeiten.nummer, true)
      } else {
        const { nummer } = await api.buchen(auftrag)
        aufGebucht(nummer)
      }
    } catch (e) {
      setFehler(fachlicherSatz(e))
    } finally {
      setLaeuft(false)
    }
  }

  const nach = katalog()
  const einheit = nach.einheiten.get(vorbelegung.einheit)
  const gezeigt = zusatzspalten.filter((s) => sichtbar.includes(s.id))

  /*
    **Das übliche Fenster** (7.2 Abschnitt 41, R1260): verschieben am Kopf,
    groß und klein im Kopf, die Ecke zieht, Esc und Kreuz schließen. Es geht
    bildschirmfüllend auf wie jedes breite Arbeitsfenster (R834) — die Maske
    trägt bis zu einundzwanzig Spalten (R369). Bis zum 2026-10-06 stand sie
    eigens gebaut 1320 Punkte breit in der Mitte, mit *Abbrechen* zweimal:
    oben im Kopf und unten neben *Buchen*. Oben steht jetzt das Kreuz.
  */
  return (
    <Dialogfenster
      titel={
        vorbelegung.bearbeiten
          ? t('beleg.berichtigen', { nummer: vorbelegung.bearbeiten.nummer })
          : t('beleg.neu')
      }
      weite="breit"
      aufSchliessen={aufSchliessen}
      kopfzusatz={
        /*
          **Die Klammer steht unveränderlich da** (R373): Gesellschaft
          und Periode. Sie kommt aus dem Ordner beziehungsweise aus der
          Kontextzeile — ein Feld daraus zu machen hieße, dem Anwender eine
          Wahl anzubieten, die der Ort schon getroffen hat. Die
          Bewertungsebene steht nicht mehr darin, sondern als Feld im
          Belegkopf: Der Ort nennt die Gruppe, gebucht wird in eine
          Schicht (R1048).
        */
        [
          einheit
            ? mitCode(einheit.code, beschriftung(einheit.namen), ' · ', einheit.schluessel)
            : gesellschaftsname(vorbelegung.einheit),
          periodenname(vorbelegung.periode),
          rahmen?.waehrung,
        ]
          .filter(Boolean)
          .join(' · ')
      }
      fuss={
        <>
          {darfSpeichern && (
            <button type="button" ref={buchenKnopf} data-buchen disabled={!buchbar} onClick={buche}>
              {t(vorbelegung.bearbeiten ? 'beleg.berichtigen.speichern' : 'beleg.speichern')}
            </button>
          )}
          <button type="button" onClick={aufSchliessen}>
            {t('beleg.abbrechen')}
          </button>
        </>
      }
    >
      <div className="nachweis belegmaske">
        {/*
          **Was fest bleibt, steht dabei** (R388). Die Klammer im Kopf
          zeigt Gesellschaft und Periode; dass sie beim Berichtigen
          *unveränderlich* sind, sieht man ihr nicht an — und wer es erst
          merkt, wenn die Datenbank es abweist, hat schon getippt.
        */}
        {vorbelegung.bearbeiten && (
          <p className="nachweisfilter festgelegt">{t('beleg.berichtigen.hinweis')}</p>
        )}
        {vorbelegung.ausKonsolidierungsspalte && (
          <p className="nachweisfilter festgelegt">{t('beleg.vorbelegt.spalte')}</p>
        )}
        {!vorbelegung.ausKonsolidierungsspalte && vorbelegung.konto && (
          <p className="nachweisfilter festgelegt">
            {t('beleg.vorbelegt', { was: kontotext(nachKonto, vorbelegung.konto) })}
          </p>
        )}

        {/* ---- Kopf: Name, Erläuterung, Begründung (R374) ---------------- */}

        <div className="belegkopf">
          <div className="feld">
            <label htmlFor="belegname">{t('beleg.name')}</label>
            <input
              id="belegname"
              ref={erstesFeld}
              value={name}
              placeholder={t('beleg.name.platzhalter')}
              aria-invalid={markiere('name') || undefined}
              onChange={(e) => setName(e.target.value)}
            />
          </div>
          {/*
            **Die Bewertungsebene neben dem Namen** (R1048): Der Platz war
            frei, und zur Wahl stehen nur Anpassungsschichten — eine Gruppe
            ist eine Summe, und die Rohdaten trägt allein die Lieferung (R396).
          */}
          <div className="feld">
            <label htmlFor="belegebene">{t('beleg.ebene')}</label>
            <Auswahlfeld
              id="belegebene"
              sorte="aufzaehlung"
              wert={ebene ?? ''}
              leer={ebene === null ? t('beleg.ebene.waehlen') : undefined}
              abgeschaltet={vorbelegung.bearbeiten !== undefined}
              eintraege={
                vorbelegung.bearbeiten
                  ? [{ wert: vorbelegung.ledger, text: bewertungsebenenname(vorbelegung.ledger) }]
                  : (rahmen?.ebenen ?? []).map((e) => ({
                      wert: e.schluessel,
                      text: beschriftung(e.namen) || e.code,
                      stillgelegtAb: e.stillgelegtAb,
                    }))
              }
              periode={vorbelegung.periode}
              aufWahl={(wert) => setGewaehlteEbene(wert || null)}
            />
          </div>
          <div className="feld breit">
            <label htmlFor="belegerlaeuterung">{t('beleg.erlaeuterung')}</label>
            <input
              id="belegerlaeuterung"
              value={erlaeuterung}
              aria-invalid={markiere('erlaeuterung') || undefined}
              onChange={(e) => setErlaeuterung(e.target.value)}
            />
          </div>
          <div className="feld breit">
            <label htmlFor="belegbegruendung">{t('beleg.begruendung')}</label>
            <input
              id="belegbegruendung"
              value={begruendung}
              aria-invalid={markiere('begruendung') || undefined}
              onChange={(e) => setBegruendung(e.target.value)}
            />
          </div>
        </div>

        {/* ---- Die Spalten auf Abruf (R369) ------------------------------ */}

        {zusatzspalten.length > 0 && (
          <fieldset className="dimensionswahl">
            <legend>{t('beleg.spalten')}</legend>
            <div className="haken">
              {zusatzspalten.map((s) => (
                <label key={s.id} className={s.leer ? 'gedaempft' : undefined}>
                  <input
                    type="checkbox"
                    checked={sichtbar.includes(s.id)}
                    disabled={s.leer}
                    onChange={(e) => {
                      const neu = e.target.checked
                        ? [...sichtbar, s.id]
                        : sichtbar.filter((x) => x !== s.id)
                      setSichtbar(neu)
                      merkeSpalten(neu)
                    }}
                  />
                  {s.kopf}
                  {s.leer && <span className="aufrissmarker">{t('beleg.spalte.leer')}</span>}
                </label>
              ))}
            </div>
            {/*
              **Dieser Satz bleibt** (R608, Entscheidung des Auftraggebers vom
              2026-09-18): Seine zweite Hälfte ist ein **Versprechen** — die
              angehakte Spalte steht beim nächsten Mal wieder da —, und ein
              Versprechen sieht man einem Kästchen nicht an.
            */}
            <p className="erlaeuterung">{t('beleg.spalten.hinweis')}</p>
          </fieldset>
        )}

        {/* ---- Die Zeilen ------------------------------------------------ */}

        {statistikbeleg && <p className="hinweis statistikhinweis">{t('beleg.statistik.hinweis')}</p>}

        <div className="tabellenrahmen">
          <table ref={spaltenrahmen} className="belegzeilen ziehbaretabelle">
            <Spaltenmassleiste masse={spaltenmasse} />
            <thead>
              <tr>
                <th>{t('beleg.spalte.konto')}</th>
                <th>{t('beleg.spalte.bewegungsart')}</th>
                <th>{t('beleg.spalte.partner')}</th>
                {gezeigt.map((s) => (
                  <th key={s.id}>{s.kopf}</th>
                ))}
                {statistikbeleg ? (
                  <th className="zahl" colSpan={2}>
                    {t('beleg.spalte.betrag')}
                  </th>
                ) : (
                  <>
                    <th className="zahl">{t('beleg.spalte.soll')}</th>
                    <th className="zahl">{t('beleg.spalte.haben')}</th>
                  </>
                )}
                <th />
              </tr>
            </thead>
            <tbody>
              {zeilen.map((z) => {
                const konto = nachKonto.get(z.konto)
                const zulaessig = (konto?.zulaessig ?? []).map((s) => nachArt.get(s)).filter(Boolean)

                return (
                  <tr key={z.id} onFocus={() => setZuletzt(z.id)}>
                    <td>
                      <Kontofeld
                        className={
                          z.konto === '' && (zahl(z.soll) !== 0 || zahl(z.haben) !== 0) ? 'offen' : undefined
                        }
                        wert={z.konto}
                        eintraege={kontenwahl}
                        ledger={vorbelegung.ledger}
                        periode={vorbelegung.periode}
                        leer={t('beleg.konto.waehlen')}
                        beschriftung={t('beleg.spalte.konto')}
                        aufWahl={(gewaehlt) => {
                          const neu = nachKonto.get(gewaehlt)
                          aendere(z.id, {
                            konto: gewaehlt,
                            // Eine Bewegungsart, die auf dem neuen Konto nichts
                            // zu suchen hat, bleibt nicht stehen (R371).
                            bewegungsart:
                              neu && neu.zulaessig.includes(z.bewegungsart) ? z.bewegungsart : '',
                          })
                        }}
                      />
                      {/* Ein Statistikkonto hängt an keiner Position von Bilanz oder GuV — gewollt (R1731). */}
                      {konto && !konto.zugeordnet && konto.kontoart !== 'statistik' && (
                        <span className="aufrissmarker">{t('beleg.konto.ohnezuordnung')}</span>
                      )}
                    </td>

                    <td>
                      {/*
                        **Auf einem Erfolgskonto gibt es kein Feld** (R372) —
                        und die Maske sagt es an der Stelle, an der es stünde.
                        Ein Feld, das ohne Begründung verschwindet, ist eine
                        Maske, die etwas hinter dem Rücken des Anwenders tut.
                      */}
                      {konto && konto.kontoart === 'statistik' ? (
                        // Ein Statistikkonto trägt keine Bewegungsart (R1729).
                        <span className="guvhinweis">{t('beleg.statistik.bewegungsart')}</span>
                      ) : konto && !konto.mitBewegungsart ? (
                        <span className="guvhinweis" title={t('beleg.guv.warum')}>
                          {t('beleg.guv.hinweis')}
                        </span>
                      ) : (
                        <Auswahlfeld
                          /* **Default steht einmal da, und vorbelegt** (R1071).
                             Bis zum 2026-10-05 gab es oben einen leeren Eintrag
                             „Default", aus dem der Dienst 060 Periodenwert
                             machte — und darunter „080 Default". Ohne Wahl
                             geht weiter NULL über die Leitung (R429), und der
                             Dienst bucht daraus 080 Default. */
                          wert={
                            z.bewegungsart ||
                            (zulaessig.some((a) => a!.schluessel === DEFAULT_BEWEGUNGSART) ? DEFAULT_BEWEGUNGSART : '')
                          }
                          abgeschaltet={!konto}
                          beschriftung={t('beleg.spalte.bewegungsart')}
                          eintraege={zulaessig.map((a) => ({
                            wert: a!.schluessel,
                            text: mitCode(a!.code, beschriftung(a!.namen), ' ', a!.schluessel),
                            suchtext: [a!.schluessel, ...Object.values(a!.namen)].join(' '),
                            stillgelegtAb: a!.stillgelegtAb,
                          }))}
                          periode={vorbelegung.periode}
                          aufWahl={(gewaehlt) => aendere(z.id, { bewegungsart: gewaehlt })}
                        />
                      )}
                    </td>

                    <td>
                      {/*
                        **Der Partner ist Pflicht als Entscheidung** (R368):
                        kein Vorgabewert, und „Default" steht als
                        ausdrückliche Wahl darin. Gespeichert wird dann NULL,
                        weil die IC-Abstimmung `ic_partner IS NOT NULL` liest.
                      */}
                      <Auswahlfeld
                        className={!z.partnerGewaehlt && z.konto !== '' && !istStatistik(z.konto) ? 'offen' : undefined}
                        wert={z.partnerGewaehlt ? z.icPartner : '?'}
                        eintraege={partnerwahl}
                        periode={vorbelegung.periode}
                        beschriftung={t('beleg.spalte.partner')}
                        titel={t('beleg.partner.pflicht')}
                        aufWahl={(gewaehlt) =>
                          aendere(z.id, {
                            icPartner: gewaehlt === '?' ? '' : gewaehlt,
                            partnerGewaehlt: gewaehlt !== '?',
                          })
                        }
                      />
                    </td>

                    {gezeigt.map((s) => (
                      <td key={s.id}>
                        {s.art === 'auswahl' ? (
                          <Auswahlfeld
                            wert={z.zusatz[s.id] ?? ''}
                            beschriftung={s.kopf}
                            /* Segment, Funktionsbereich und die
                               Analysedimensionen des Kunden: ohne Wahl der
                               Vorgabewert, nicht die Abwesenheit einer
                               Angabe (R429). Anders als beim Partner gibt es
                               hier keinen unentschiedenen Zustand. */
                            leer={ohneWert('weitere')}
                            eintraege={(s.vorrat ?? []).map((v) => ({
                              wert: v.schluessel,
                              text: mitCode(v.code, beschriftung(v.namen), ' ', v.schluessel),
                              suchtext: [v.schluessel, ...Object.values(v.namen)].join(' '),
                              stillgelegtAb: v.stillgelegtAb,
                            }))}
                            periode={vorbelegung.periode}
                            aufWahl={(gewaehlt) =>
                              aendere(z.id, { zusatz: { ...z.zusatz, [s.id]: gewaehlt } })
                            }
                          />
                        ) : s.art === 'periode' ? (
                          /*
                            **Dasselbe Periodenfeld wie überall** (R839 — Eine
                            Periode liest sich als Monat und Jahr; R502 — Am
                            Bildschirm steht MM.JJJJ). Bis zum 2026-09-30 stand
                            hier ein freies Feld, das die Zahl `202611`
                            verlangte.

                            **Die Zeile merkt sich, was getippt wurde, nicht
                            nur, was sich lesen ließ.** Das Periodenfeld meldet
                            für „leer" und für „unlesbar" dasselbe; die Maske
                            muss die beiden aber unterscheiden, weil nur das
                            zweite die Buchung aufhält. Die Hülle liest deshalb
                            den Text mit, das Feld darin bleibt das eine
                            Bauteil — mit seinem Hinweis und seinem Satz bei
                            einer Fehleingabe.
                          */
                          <span
                            className="periodenwert"
                            onChange={(e) => {
                              if (e.target instanceof HTMLInputElement) {
                                aendere(z.id, { zusatz: { ...z.zusatz, [s.id]: e.target.value } })
                              }
                            }}
                          >
                            <Periodenfeld
                              klasse="nurfeld"
                              beschriftung={s.kopf}
                              wert={ausPeriodenfeld(z.zusatz[s.id] ?? '')}
                              aufWert={() => {}}
                            />
                          </span>
                        ) : (
                          <input
                            value={z.zusatz[s.id] ?? ''}
                            aria-label={s.kopf}
                            onChange={(e) =>
                              aendere(z.id, { zusatz: { ...z.zusatz, [s.id]: e.target.value } })
                            }
                          />
                        )}
                      </td>
                    ))}

                    {statistikbeleg ? (
                      <td className="zahl" colSpan={2}>
                        <input
                          className="betrag"
                          value={z.soll !== '' || z.haben === '' ? z.soll : `-${z.haben}`}
                          inputMode="decimal"
                          aria-label={t('beleg.spalte.betrag')}
                          onChange={(e) => aendere(z.id, { soll: e.target.value, haben: '' })}
                        />
                      </td>
                    ) : (
                    <>
                    <td className="zahl">
                      <input
                        className="betrag"
                        value={z.soll}
                        inputMode="decimal"
                        aria-label={t('beleg.spalte.soll')}
                        placeholder={z.haben === '' ? gegenvorschlag(z.id, 'soll') : ''}
                        onKeyDown={(e) => uebernimm(e, z, 'soll')}
                        onChange={(e) => aendere(z.id, { soll: e.target.value, haben: '' })}
                      />
                    </td>
                    <td className="zahl">
                      <input
                        className="betrag"
                        value={z.haben}
                        inputMode="decimal"
                        aria-label={t('beleg.spalte.haben')}
                        placeholder={z.soll === '' ? gegenvorschlag(z.id, 'haben') : ''}
                        onKeyDown={(e) => uebernimm(e, z, 'haben')}
                        onChange={(e) => aendere(z.id, { haben: e.target.value, soll: '' })}
                      />
                    </td>
                    </>
                    )}
                    <td>
                      <button
                        type="button"
                        className="schmal"
                        title={t('beleg.zeile.weg')}
                        disabled={zeilen.length <= 2}
                        onClick={() => setZeilen((alt) => alt.filter((x) => x.id !== z.id))}
                      >
                        ×
                      </button>
                    </td>
                  </tr>
                )
              })}
            </tbody>
            {!statistikbeleg && (
            <tfoot>
              <tr className="summe">
                <td colSpan={3 + gezeigt.length}>{t('beleg.summe.soll')} / {t('beleg.summe.haben')}</td>
                <td className="zahl"><Betrag wert={summeSoll} /></td>
                <td className="zahl"><Betrag wert={summeHaben} /></td>
                <td />
              </tr>
            </tfoot>
            )}
          </table>
        </div>

        <button
          type="button"
          className="schmal"
          onClick={() => setZeilen((alt) => [...alt, leereZeile()])}
        >
          + {t('beleg.zeile.hinzu')}
        </button>

        {/*
          **Die Differenz steht unter den Zeilen und nicht in einer Meldung
          beim Speichern** (R370): Wer bucht, sieht beim Tippen, ob es aufgeht.
          Die Datenbank bleibt die entscheidende Instanz (R195); die Maske
          nimmt sie nicht weg, sie zeigt sie nur vorher.

          **Bei Differenz null steht nie „es fehlen 0,00"** (R1054): Geht es
          auf, aber fehlt noch ein Konto, steht hier nichts — das offene
          Kontofeld sagt, wo.
        */}
        {gemischt && <p className="probe abweichung">{t('beleg.statistik.gemischt')}</p>}
        {!statistikbeleg && !gemischt && (differenz !== 0 || belegt.length >= 2) && (
        <p className={`probe ${differenz === 0 ? 'stimmt' : 'abweichung'}`}>
          {differenz === 0
            ? t('beleg.gehtauf')
            : t('beleg.gehtnichtauf', { betrag: formatiert(Math.abs(differenz)) })}
          {ausgleich && (
            <button
              type="button"
              className="schmal ausgleichen"
              onClick={() =>
                aendere(
                  ausgleich.id,
                  ausgleich.seite === 'soll'
                    ? { soll: ausgleich.betrag, haben: '' }
                    : { haben: ausgleich.betrag, soll: '' },
                )
              }
            >
              {t('beleg.ausgleichen', {
                zeile: String(ausgleich.nummer),
                betrag: ausgleich.betrag,
                seite: t(ausgleich.seite === 'soll' ? 'beleg.spalte.soll' : 'beleg.spalte.haben'),
              })}
            </button>
          )}
        </p>
        )}

        {partnerOffen && belegt.length > 0 && (
          <p className="hinweis">{t('beleg.partner.pflicht')}</p>
        )}
        {periodeUnlesbar && <p className="hinweis">{t('periode.ungueltig')}</p>}
        {!pflichtVollstaendig && (
          <p className="hinweis">
            {t('beleg.pflicht.fehlt', {
              felder: fehlend.map((f) => t(`beleg.pflicht.${f}`)).join(', '),
            })}
          </p>
        )}
        {fehler && <p className="hinweis">{fehler}</p>}
      </div>
    </Dialogfenster>
  )
}

/**
 * Die Zeilen, mit denen die Maske aufgeht.
 *
 * **Zwei leere Zeilen sind das Mindeste** — eine Buchung hat zwei Seiten. Aus
 * dem Bericht kommt das Konto der angeklickten Zelle in die erste; die zweite
 * bleibt leer, denn was gebucht wird, weiß der Anwender und nicht die Zelle
 * (R386).
 */
function anfangszeilen(v: Buchungsvorbelegung): Maskenzeile[] {
  // Berichtigen fängt beim vorhandenen Beleg an — Zeilen samt Beträgen.
  if (v.bearbeiten) {
    return v.bearbeiten.zeilen.map(ausErfassungszeile)
  }

  if (v.duplikat) {
    return v.duplikat.zeilen.map(ausErfassungszeile)
  }

  // **Die Vorlage trägt alle Merkmale und keine Zahl** (R376).
  if (v.vorlage) {
    return v.vorlage.map((z) => ({ ...ausErfassungszeile(z), soll: '', haben: '' }))
  }

  const erste = leereZeile()

  if (!v.ausKonsolidierungsspalte && v.konto) {
    erste.konto = v.konto
  }

  // Was der Aufriss festgelegt hat, reist mit — Partner, Segment, Kostenstelle
  // (R385). Ein aufgerissener Partner ist zugleich eine getroffene Wahl.
  for (const [achse, wert] of Object.entries(v.weitere ?? {})) {
    if (wert === null) continue
    if (achse === 'partner') {
      erste.icPartner = wert
      erste.partnerGewaehlt = true
    } else if (achse === 'segment') {
      erste.zusatz.segment = wert
    } else if (/^dim[1-8]$/.test(achse)) {
      erste.zusatz[achse] = wert
    }
  }

  return [erste, leereZeile()]
}

function ausErfassungszeile(z: Erfassungszeile): Maskenzeile {
  const zusatz: Record<string, string> = {}
  for (const feld of ['segment', 'funktionsbereich', 'transaktionswaehrung', 'erlaeuterung'] as const) {
    if (z[feld]) zusatz[feld] = String(z[feld])
  }
  for (let d = 1; d <= 8; d++) {
    const wert = z[`dim${d}` as keyof Erfassungszeile]
    if (wert) zusatz[`dim${d}`] = String(wert)
  }

  return {
    id: naechsteId++,
    konto: z.konto,
    bewegungsart: z.bewegungsart ?? '',
    icPartner: z.icPartner ?? '',
    // Ein Duplikat hat die Wahl schon getroffen: Der Partner stand im
    // Ursprungsbeleg, auch wenn er dort „keiner" war.
    partnerGewaehlt: true,
    soll: z.betrag > 0 ? formatiert(z.betrag) : '',
    haben: z.betrag < 0 ? formatiert(-z.betrag) : '',
    zusatz,
  }
}

function kontotext(karte: Map<string, Buchbaresonto>, schluessel: string): string {
  const k = karte.get(schluessel)
  // Ohne Buchungsrahmen fragt der Satz den Katalog (R1220): Bis zum 2026-10-06
  // stand hier „Aus dem Bericht übernommen: KTO-6900", solange der Rahmen lud.
  return k ? kontoname(k) : kontobezeichnung(schluessel)
}

/** Die Spaltenwahl liegt beim Anwender und nicht im Programm (L2, R369). */
/**
 * Was ab Werk angehakt ist.
 *
 * **Der Buchungstext, und sonst nichts** (R395). 2.16 Abschnitt 3.3 lässt die
 * übrigen Spalten ausgeblendet — das sind *Dimensionen*, und der Auftrag war
 * ausdrücklich, sie erst auf Wunsch hereinzuholen. Der Buchungstext ist keine
 * Dimension, sondern ein Satz, und der Auftraggeber hat ihn am 2026-09-12
 * eigens verlangt: *„Das hilft enorm in der Analyse."* Ein Feld, das man erst
 * unter elf Ankreuzfeldern finden muss, wird nicht benutzt — und dann steht im
 * Wertenachweis nichts, wo etwas stehen sollte.
 */
const ABWERK = ['erlaeuterung']

function gemerkteSpalten(): string[] {
  try {
    const roh = window.localStorage.getItem(MERKSCHLUESSEL)
    return roh ? (JSON.parse(roh) as string[]) : ABWERK
  } catch {
    return ABWERK
  }
}

function merkeSpalten(spalten: string[]): void {
  try {
    window.localStorage.setItem(MERKSCHLUESSEL, JSON.stringify(spalten))
  } catch {
    // Ein Browser ohne Speicher ist kein Grund, die Maske nicht zu bedienen.
  }
}
